import { useEffect, useRef } from "react";
import { Remove as RemoveIcon, Check as CheckIcon, Build as BuildIcon } from "@mui/icons-material";
import "./StagesProgressComponent.css";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation, A11y } from "swiper/modules";

const StageStep = ({
  stage: { name, not_started, finished, late, over_year_late, in_progress },
  index,
  total,
  isSelected,
  hideDelays,
  compact,
  onChangeStage,
  tabIndex,
}: {
  role: string;
  stage: any;
  index: number;
  total: number;
  isSelected: boolean;
  hideDelays: boolean;
  compact: boolean;
  onChangeStage: any;
  tabIndex: number;
  tab: string;
}) => {
  const totalSections = not_started + finished + late + over_year_late + in_progress;
  const completed = totalSections === 0 ? 0 : Math.round((finished * 100) / totalSections);
  let mediumDelay = totalSections === 0 ? 0 : Math.round((late * 100) / totalSections);
  let highDelay = totalSections === 0 ? 0 : Math.round((over_year_late * 100) / totalSections);
  let inProgress = totalSections === 0 ? 0 : Math.round((in_progress * 100) / totalSections);
  const notStarted = totalSections === 0 ? 0 : Math.round((not_started * 100) / totalSections);

  if (hideDelays) {
    inProgress = inProgress + mediumDelay + highDelay;
    mediumDelay = 0;
    highDelay = 0;
  }

  const status = completed === 100 ? "completed" : notStarted === 100 ? "not-started" : "in-progress";

  const severityColor = highDelay > 0 ? "#FE776A" : mediumDelay > 0 ? "#F6C24B" : "#FFF";

  let IconComponent;
  if (status === "completed") IconComponent = CheckIcon;
  else if (status === "in-progress") IconComponent = BuildIcon;
  else IconComponent = RemoveIcon;

  const ringStyle: React.CSSProperties = {
    background: `conic-gradient(
    #5ECCA3      0%   ${completed}%,
    #007FFF      ${completed}% ${completed + inProgress}%,
    #F6C24B      ${completed}% ${completed + inProgress + mediumDelay}%,
    #FE776A      ${completed + inProgress + mediumDelay}%    ${completed + inProgress + mediumDelay + highDelay}%,
    #DDE3EA      ${completed + inProgress + mediumDelay + highDelay}%    100%
  )`,
  };

  useEffect(() => {
    document.querySelector(".swiper-button-next")?.setAttribute("aria-label", "כפתור הבא");
    document.querySelector(".swiper-button-prev")?.setAttribute("aria-label", "כפתור קודם");
  }, []);

  const handleClick = (idx: number) => onChangeStage?.(idx - 1);

  return (
    <div
      aria-label={`${name}, שלב ${index}/${total}: ${notStarted}% טרם החל, ${inProgress}% בתהליך, ${mediumDelay}% עיכוב בינוני, ${highDelay}% עיכוב גבוה, ${completed}% הסתיים`}
      aria-current={isSelected ? "page" : undefined}
      role={"link"}
      onClick={() => handleClick(index)}
      className={"stage-step"}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleClick(index);
        }
      }}
    >
      {compact && (
        <div className={`stage-step ${status} ${isSelected ? "selected" : ""}`}>
          <div style={ringStyle} className="outer-ring compact">
            <div className="inner-padding-circle compact">
              <div className={isSelected ? `inner-circle compact ${status}` : `inner-circle compact ${status}`}>
                <IconComponent
                  role={"img"}
                  aria-hidden={"false"}
                  aria-label={`
                    ${notStarted}% טרם החל,
                    ${inProgress}% בתהליך,
                    ${mediumDelay}% עיכוב בינוני,
                    ${highDelay}% עיכוב גבוה,
                    ${completed}% הסתיים`}
                  style={{ color: status === "completed" ? "#FFF" : severityColor, width: 24, height: 24 }}
                />
              </div>
            </div>
          </div>
          <div className="step-name">{name}</div>
        </div>
      )}

      {!compact && (
        <div className={`stage-step ${status} ${isSelected ? "selected" : ""}`}>
          <div className="step-name">{name}</div>
          <div className="outer-ring" style={ringStyle}>
            <div className="inner-padding-circle">
              <div className={isSelected ? `inner-circle ${status}` : `inner-circle ${status}`}>
                <IconComponent
                  role={"img"}
                  aria-hidden={"false"}
                  aria-label={`
                                      ${notStarted}% טרם החל,
                                      ${inProgress}% בתהליך,
                                      ${mediumDelay}% עיכוב בינוני,
                                      ${highDelay}% עיכוב גבוה,
                                      ${completed}% הסתיים
                                    `}
                  style={{
                    color: status === "completed" ? "#FFF" : severityColor,
                    width: 24,
                    height: 24,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="step-index">
            שלב {index}/{total}
          </div>
        </div>
      )}

      {isSelected && <div className="underline" />}
    </div>
  );
};

export default function StagesProgressComponent({
  stages,
  selectedIndex = 0,
  hideDelays = true,
  compact = false,
  onChangeStage,
}: {
  stages: any;
  selectedIndex: number;
  hideDelays: boolean;
  compact?: boolean;
  onChangeStage: any;
}) {
  const total = stages.length;

  const slidesRef = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (slidesRef.current[selectedIndex]) slidesRef.current[selectedIndex]?.focus();
  }, [selectedIndex]);

  return (
    <Swiper
      // dir={'ltr'}
      className={compact ? "" : "stages-swiper"}
      modules={[A11y, Navigation]}
      navigation
      style={{ width: "100%", minHeight: "1px", marginBottom: "30px" }}
      centeredSlides={false}
      centerInsufficientSlides={true}
      onSwiper={(swiper: any) => (swiper.current = swiper)}
      onSlideChange={(swiper: any) => {
        // only on mobile (where slidesPerView === 1)
        if (swiper.params.slidesPerView === 1) {
          onChangeStage(swiper.activeIndex);
        }
      }}
      breakpoints={{
        320: {
          slidesPerView: compact ? 2 : 1,
        },
        600: {
          slidesPerView: compact ? 2 : 1,
        },
        900: {
          slidesPerView: compact ? 6 : 4,
        },
        1200: {
          slidesPerView: compact ? 9 : 5,
        },
      }}
    >
      {stages.map((stage: any, idx: number) => (
        <SwiperSlide key={idx}>
          <div
            ref={(el) => {
              slidesRef.current[idx] = el;
            }}
            style={{ outline: "none" }}
          >
            <StageStep
              role="link"
              aria-current="page"
              key={idx}
              tabIndex={0}
              tab="role"
              stage={stage}
              index={idx + 1}
              total={total}
              isSelected={idx === selectedIndex}
              onChangeStage={onChangeStage}
              hideDelays={hideDelays}
              compact={compact}
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
